import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Gift, Check, Sparkles, Star, Crown, Tablet, Layers, ExternalLink } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import {
  fetchRothusDigitalProducts,
  findDigitalProduct,
  rothusCheckoutUrl,
  MEIN_ROTHUS_URL,
  GLIDE_DIGITAL_CODE,
  COMBO_DIGITAL_CODE,
  type RothusDigitalProduct,
} from '@/lib/rothusDigital';

interface SubscriptionFormData {
  salutation: string;
  first_name: string;
  last_name: string;
  email: string;
  address: string;
  zip: string;
  city: string;
  country: string;
  subscription_type: string;
  is_gift: boolean;
  gift_salutation: string;
  gift_first_name: string;
  gift_last_name: string;
  gift_email: string;
  gift_address: string;
  gift_zip: string;
  gift_city: string;
  gift_country: string;
  message: string;
  accepts_agb: boolean;
  newsletter_signup: boolean;
}

const initialFormData: SubscriptionFormData = {
  salutation: '',
  first_name: '',
  last_name: '',
  email: '',
  address: '',
  zip: '',
  city: '',
  country: 'Schweiz',
  subscription_type: 'yearly',
  is_gift: false,
  gift_salutation: '',
  gift_first_name: '',
  gift_last_name: '',
  gift_email: '',
  gift_address: '',
  gift_zip: '',
  gift_city: '',
  gift_country: 'Schweiz',
  message: '',
  accepts_agb: false,
  newsletter_signup: false,
};

const subscriptionOptions = [
  { 
    id: 'yearly', 
    label: 'Jahresabo', 
    sublabel: '1 Jahr · 4 gedruckte Ausgaben',
    price: '49', 
    popular: true,
    icon: Star,
    cta: 'Jetzt abonnieren',
    benefits: ['4 gedruckte Ausgaben', 'Digitalzugang inklusive', 'Zugang zum digitalen Archiv', 'Kostenloser Versand']
  },
  { 
    id: 'twoyear', 
    label: '2-Jahres-Abo', 
    sublabel: '2 Jahre · 8 gedruckte Ausgaben',
    price: '78', 
    popular: false,
    icon: Sparkles,
    savings: 'Du sparst CHF 20',
    cta: '2 Jahre glide sichern',
    benefits: ['8 gedruckte Ausgaben', 'Digitalzugang inklusive', 'Zugang zum digitalen Archiv', 'Kostenloser Versand', 'Preisvorteil']
  },
  { 
    id: 'kombiabo', 
    label: 'Kombi-Abo', 
    sublabel: 'glide + Wandermagazin SCHWEIZ (Print)',
    price: '98', 
    popular: false,
    icon: Crown,
    cta: 'Beide Magazine abonnieren',
    benefits: ['4 Ausgaben glide', '4 Ausgaben Wandermagazin SCHWEIZ', 'Digitalzugang zu beiden Magazinen inklusive', 'Kostenloser Versand']
  },
];

/** Reine Digitalabos – Preise kommen zentral von Rothus Medien. */
const digitalOptions = [
  {
    code: GLIDE_DIGITAL_CODE,
    label: 'glide Digital',
    sublabel: '12 Monate · digitaler Zugang',
    icon: Tablet,
    cta: 'Digitalabo bestellen',
    fallbackPrice: 29,
    benefits: [
      'Alle glide-Ausgaben digital lesen',
      'Zugriff über «Mein Rothus»',
      'Auf Smartphone, Tablet und Computer',
      '12 Monate Zugang',
      'Keine automatische Verlängerung',
    ],
  },
  {
    code: COMBO_DIGITAL_CODE,
    label: 'glide + Wandermagazin Digital',
    sublabel: '12 Monate · beide Magazine digital',
    icon: Layers,
    cta: 'Beide Magazine digital lesen',
    fallbackPrice: 49,
    benefits: [
      'Alle glide-Ausgaben digital lesen',
      'Alle Wandermagazin-Ausgaben digital lesen',
      'Zugriff über «Mein Rothus»',
      'Auf Smartphone, Tablet und Computer',
      '12 Monate Zugang',
      'Keine automatische Verlängerung',
    ],
  },
];

const SubscriptionSection: React.FC = () => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<SubscriptionFormData>(initialFormData);
  const [showDialog, setShowDialog] = useState(false);
  const [mode, setMode] = useState<'print' | 'digital'>('print');
  const [digitalProducts, setDigitalProducts] = useState<RothusDigitalProduct[]>([]);

  useEffect(() => {
    let active = true;
    fetchRothusDigitalProducts().then((p) => {
      if (active) setDigitalProducts(p);
    });
    return () => {
      active = false;
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const openOrderDialog = (subscriptionId: string) => {
    setFormData(prev => ({ ...prev, subscription_type: subscriptionId }));
    setShowDialog(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.accepts_agb) {
      toast({
        title: 'Bitte akzeptieren Sie die AGB',
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const selectedOption = subscriptionOptions.find(o => o.id === formData.subscription_type);
      const amount_chf = selectedOption ? Number(selectedOption.price) : null;

      const { error } = await supabase
        .from('magazine_orders')
        .insert([{
          salutation: formData.salutation,
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          address: formData.address,
          zip: formData.zip,
          city: formData.city,
          country: formData.country,
          subscription_type: formData.subscription_type,
          amount_chf,
          is_gift: formData.is_gift,
          gift_salutation: formData.is_gift ? formData.gift_salutation : null,
          gift_first_name: formData.is_gift ? formData.gift_first_name : null,
          gift_last_name: formData.is_gift ? formData.gift_last_name : null,
          gift_email: formData.is_gift ? formData.gift_email : null,
          gift_address: formData.is_gift ? formData.gift_address : null,
          gift_zip: formData.is_gift ? formData.gift_zip : null,
          gift_city: formData.is_gift ? formData.gift_city : null,
          gift_country: formData.is_gift ? formData.gift_country : null,
          message: formData.message || null,
          status: 'new'
        }]);


      if (error) throw error;

      await supabase.functions.invoke('send-order-confirmation', {
        body: {
          email: formData.email,
          firstName: formData.first_name,
          subscriptionType: formData.subscription_type,
          isGift: formData.is_gift,
          giftFirstName: formData.gift_first_name,
          giftLastName: formData.gift_last_name,
          giftAddress: formData.gift_address,
          giftZip: formData.gift_zip,
          giftCity: formData.gift_city
        }
      });

      await supabase.functions.invoke('send-order-notification', {
        body: {
          salutation: formData.salutation,
          firstName: formData.first_name,
          lastName: formData.last_name,
          email: formData.email,
          address: formData.address,
          zip: formData.zip,
          city: formData.city,
          country: formData.country,
          subscriptionType: formData.subscription_type,
          isGift: formData.is_gift,
          giftSalutation: formData.gift_salutation,
          giftFirstName: formData.gift_first_name,
          giftLastName: formData.gift_last_name,
          giftAddress: formData.gift_address,
          giftZip: formData.gift_zip,
          giftCity: formData.gift_city,
          giftCountry: formData.gift_country,
          message: formData.message
        }
      });

      if (formData.newsletter_signup) {
        await supabase
          .from('newsletter_subscribers')
          .insert([{
            email: formData.email,
            first_name: formData.first_name,
            last_name: formData.last_name,
            active: true
          }]);
      }

      toast({
        title: 'Bestellung erfolgreich!',
        description: 'Sie erhalten in Kürze eine Bestätigung per E-Mail.',
      });

      setFormData(initialFormData);
      setShowDialog(false);

    } catch (error) {
      console.error('Error submitting order:', error);
      toast({
        title: 'Fehler bei der Bestellung',
        description: 'Bitte versuchen Sie es später erneut.',
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const selectedOption = subscriptionOptions.find(opt => opt.id === formData.subscription_type);

  return (
    <section id="abonnements" className="py-20 bg-background scroll-mt-24">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-10 max-w-2xl mx-auto">
          <p className="text-xs uppercase tracking-[0.2em] text-primary mb-3">01 — Abo</p>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3">
            Dein glide Abo
          </h2>
          <p className="text-muted-foreground">Vier Ausgaben. Ein Jahr Langlauf im Kopf.</p>
        </div>

        {/* Umschalter Print / Digital */}
        <div className="flex justify-center mb-6">
          <div
            role="tablist"
            aria-label="Abo-Art wählen"
            className="inline-flex w-full max-w-md p-1 rounded-lg bg-muted"
          >
            {([
              { id: 'print', label: 'Print + Digital' },
              { id: 'digital', label: 'Nur Digital' },
            ] as const).map((tab) => (
              <button
                key={tab.id}
                role="tab"
                type="button"
                aria-selected={mode === tab.id}
                onClick={() => setMode(tab.id)}
                className={`flex-1 px-4 py-2.5 text-sm font-medium rounded-md transition-all ${
                  mode === tab.id
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <p className="text-center text-sm text-muted-foreground mb-10 max-w-xl mx-auto">
          {mode === 'print'
            ? 'Jedes Printabo enthält den digitalen Zugang zum Magazin.'
            : 'Reine Digitalabos – ohne gedruckte Ausgaben, lesbar über «Mein Rothus».'}
        </p>

        {/* Subscription Cards */}
        {mode === 'print' ? (
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {subscriptionOptions.map((option, index) => {
            const Icon = option.icon;
            const isSelected = formData.subscription_type === option.id;
            
            return (
              <Card 
                key={option.id}
                className={`relative overflow-hidden transition-all duration-500 hover:shadow-2xl hover:-translate-y-1 cursor-pointer group ${
                  option.popular 
                    ? 'border-primary shadow-lg ring-1 ring-primary/20' 
                    : 'hover:border-primary/50'
                }`}
                onClick={() => openOrderDialog(option.id)}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {/* Popular Badge */}
                {option.popular && (
                  <div className="absolute top-0 right-0">
                    <div className="bg-primary text-primary-foreground text-xs font-semibold px-4 py-1.5 rounded-bl-lg">
                      Beliebt
                    </div>
                  </div>
                )}

                {/* Savings Badge */}
                {option.savings && (
                  <div className="absolute top-0 right-0">
                    <div className="bg-green-500 text-white text-xs font-semibold px-4 py-1.5 rounded-bl-lg">
                      {option.savings}
                    </div>
                  </div>
                )}

                <CardContent className="p-6">
                  {/* Icon */}
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110 ${
                    option.popular ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'
                  }`}>
                    <Icon className="h-6 w-6" />
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-bold text-foreground mb-1">{option.label}</h3>
                  <p className="text-sm text-muted-foreground mb-4">{option.sublabel}</p>

                  {/* Price */}
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-foreground">CHF {option.price}</span>
                    <span className="text-muted-foreground">.-</span>
                  </div>

                  {/* Benefits */}
                  <ul className="space-y-2 mb-6">
                    {option.benefits.map((benefit, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <Check className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                        {benefit}
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  <Button 
                    className={`w-full transition-all ${
                      option.popular 
                        ? 'bg-primary hover:bg-primary/90' 
                        : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                    }`}
                  >
                    {option.cta}

                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
        ) : (
        <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {digitalOptions.map((option) => {
            const Icon = option.icon;
            const product = findDigitalProduct(digitalProducts, option.code);
            const price = product?.price_chf ?? option.fallbackPrice;

            return (
              <Card
                key={option.code}
                className="relative overflow-hidden transition-all duration-500 hover:shadow-2xl hover:-translate-y-1 hover:border-primary/50"
              >
                <CardContent className="p-6 flex flex-col h-full">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 bg-muted text-foreground">
                    <Icon className="h-6 w-6" />
                  </div>

                  <h3 className="text-xl font-bold text-foreground mb-1">{option.label}</h3>
                  <p className="text-sm text-muted-foreground mb-4">{option.sublabel}</p>

                  <div className="mb-6">
                    <span className="text-4xl font-bold text-foreground">CHF {price}</span>
                    <span className="text-muted-foreground">.-</span>
                  </div>

                  <ul className="space-y-2 mb-6">
                    {option.benefits.map((benefit, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <Check className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                        {benefit}
                      </li>
                    ))}
                  </ul>

                  <Button asChild className="w-full mt-auto bg-primary hover:bg-primary/90">
                    <a href={rothusCheckoutUrl(option.code)} target="_blank" rel="noopener noreferrer">
                      {option.cta}
                      <ExternalLink className="ml-2 h-4 w-4" />
                    </a>
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
        )}

        {/* Hinweis für bestehende Printabonnenten */}
        <div className="max-w-3xl mx-auto mt-8 p-5 rounded-lg border border-border bg-muted/40 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1">
            <p className="font-medium text-foreground">Schon glide Print-Abonnent?</p>
            <p className="text-sm text-muted-foreground">
              Der digitale Zugang ist in Ihrem Printabo bereits enthalten. Melden Sie sich einfach
              bei «Mein Rothus» an.
            </p>
          </div>
          <Button asChild variant="outline" className="sm:w-auto w-full">
            <a href={MEIN_ROTHUS_URL} target="_blank" rel="noopener noreferrer">
              Zu Mein Rothus
              <ExternalLink className="ml-2 h-4 w-4" />
            </a>
          </Button>
        </div>
      </div>


      {/* Order Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="h-5 w-5 text-primary" />
              {selectedOption?.label} bestellen
            </DialogTitle>
          </DialogHeader>

          {/* Selected Plan Summary */}
          <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg mb-4">
            <div>
              <p className="font-semibold text-foreground">{selectedOption?.label}</p>
              <p className="text-sm text-muted-foreground">{selectedOption?.sublabel}</p>
            </div>
            <div className="text-right">
              <span className="text-2xl font-bold text-primary">CHF {selectedOption?.price}.-</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Personal Info */}
            <div>
              <Label htmlFor="salutation">Anrede</Label>
              <Select value={formData.salutation} onValueChange={(v) => handleSelectChange('salutation', v)}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Bitte wählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Herr">Herr</SelectItem>
                  <SelectItem value="Frau">Frau</SelectItem>
                  <SelectItem value="Familie">Familie</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="first_name">Vorname *</Label>
                <Input
                  id="first_name"
                  name="first_name"
                  required
                  value={formData.first_name}
                  onChange={handleInputChange}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="last_name">Nachname *</Label>
                <Input
                  id="last_name"
                  name="last_name"
                  required
                  value={formData.last_name}
                  onChange={handleInputChange}
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email">E-Mail *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleInputChange}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="address">Adresse *</Label>
              <Input
                id="address"
                name="address"
                required
                value={formData.address}
                onChange={handleInputChange}
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="zip">PLZ *</Label>
                <Input
                  id="zip"
                  name="zip"
                  required
                  value={formData.zip}
                  onChange={handleInputChange}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="city">Ort *</Label>
                <Input
                  id="city"
                  name="city"
                  required
                  value={formData.city}
                  onChange={handleInputChange}
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="country">Land</Label>
              <Select value={formData.country} onValueChange={(v) => handleSelectChange('country', v)}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Schweiz">Schweiz</SelectItem>
                  <SelectItem value="Deutschland">Deutschland</SelectItem>
                  <SelectItem value="Österreich">Österreich</SelectItem>
                  <SelectItem value="Liechtenstein">Liechtenstein</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Gift Option */}
            <div className="flex items-center space-x-3 p-4 bg-primary/5 rounded-lg border border-primary/10">
              <Checkbox
                id="is_gift"
                checked={formData.is_gift}
                onCheckedChange={(checked) => 
                  setFormData(prev => ({ ...prev, is_gift: checked as boolean }))
                }
              />
              <Label htmlFor="is_gift" className="cursor-pointer flex items-center gap-2">
                <Gift className="h-4 w-4 text-primary" />
                Als Geschenk bestellen
              </Label>
            </div>

            {/* Gift Fields */}
            {formData.is_gift && (
              <div className="space-y-3 p-4 bg-primary/5 rounded-lg border border-primary/20 animate-fade-in">
                <h4 className="font-medium flex items-center gap-2">
                  <Gift className="h-4 w-4" />
                  Geschenk-Empfänger
                </h4>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="gift_first_name">Vorname</Label>
                    <Input
                      id="gift_first_name"
                      name="gift_first_name"
                      value={formData.gift_first_name}
                      onChange={handleInputChange}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="gift_last_name">Nachname</Label>
                    <Input
                      id="gift_last_name"
                      name="gift_last_name"
                      value={formData.gift_last_name}
                      onChange={handleInputChange}
                      className="mt-1"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="gift_address">Adresse</Label>
                  <Input
                    id="gift_address"
                    name="gift_address"
                    value={formData.gift_address}
                    onChange={handleInputChange}
                    className="mt-1"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="gift_zip">PLZ</Label>
                    <Input
                      id="gift_zip"
                      name="gift_zip"
                      value={formData.gift_zip}
                      onChange={handleInputChange}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="gift_city">Ort</Label>
                    <Input
                      id="gift_city"
                      name="gift_city"
                      value={formData.gift_city}
                      onChange={handleInputChange}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Message */}
            <div>
              <Label htmlFor="message">Nachricht (optional)</Label>
              <textarea
                id="message"
                name="message"
                rows={2}
                value={formData.message}
                onChange={handleInputChange}
                className="mt-1 w-full px-3 py-2 border border-border rounded-md bg-background text-foreground resize-none"
              />
            </div>

            {/* Checkboxes */}
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Checkbox
                  id="newsletter_signup"
                  checked={formData.newsletter_signup}
                  onCheckedChange={(checked) => 
                    setFormData(prev => ({ ...prev, newsletter_signup: checked as boolean }))
                  }
                />
                <Label htmlFor="newsletter_signup" className="cursor-pointer text-sm">
                  Newsletter abonnieren
                </Label>
              </div>

              <div className="flex items-start space-x-3">
                <Checkbox
                  id="accepts_agb"
                  checked={formData.accepts_agb}
                  onCheckedChange={(checked) => 
                    setFormData(prev => ({ ...prev, accepts_agb: checked as boolean }))
                  }
                  className="mt-0.5"
                />
                <Label htmlFor="accepts_agb" className="cursor-pointer text-sm">
                  Ich akzeptiere die <a href="/agb" className="text-primary underline" target="_blank">AGB</a> *
                </Label>
              </div>
            </div>

            <Button type="submit" className="w-full" size="lg" disabled={isLoading}>
              {isLoading ? 'Wird verarbeitet...' : `Jetzt für CHF ${selectedOption?.price}.- bestellen`}
            </Button>

            <p className="text-xs text-muted-foreground text-center">
              Die Rechnung wird per Post zugestellt.
            </p>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default SubscriptionSection;
